import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Users2, Camera, Home, Umbrella, UserX, RefreshCw } from 'lucide-react';

// Groups the /attendance/team-operations response into 4 buckets and shows a live tile.
// Auto-refreshes every 60 seconds. Visible to every authenticated team member.
const TeamNowWidget = () => {
  const [ops, setOps] = useState([]);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOps = useCallback(async () => {
    try {
      const r = await api.get('/attendance/team-operations');
      setOps(r.data || []);
      setLastFetchedAt(new Date());
    } catch {
      // silent — dashboard shouldn't break if this endpoint hiccups
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOps();
    const id = setInterval(fetchOps, 60_000);
    return () => clearInterval(id);
  }, [fetchOps]);

  const buckets = {
    in_studio: ops.filter(o => o.status === 'present'),
    on_shoot: ops.filter(o => o.status === 'on_shoot'),
    wfh: ops.filter(o => o.status === 'wfh'),
    leave: ops.filter(o => o.status === 'leave'),
    absent: ops.filter(o => o.status === 'absent'),
  };

  const asOf = lastFetchedAt
    ? lastFetchedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : '—';

  if (loading && ops.length === 0) {
    return (
      <Card className="bg-[#121212] border-white/10" data-testid="team-now-widget">
        <CardContent className="p-4 text-sm text-zinc-500 text-center">Loading team status…</CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#121212] border-white/10" data-testid="team-now-widget">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users2 className="h-4 w-4 text-[#F5A623]" />
            <h3 className="text-white font-semibold text-sm">In Studio Right Now</h3>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-zinc-500">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live · updated {asOf}
            <button onClick={fetchOps} className="text-zinc-400 hover:text-white ml-1" title="Refresh now" data-testid="team-now-refresh">
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-2">
          <Bucket icon={Users2} label="In Studio" count={buckets.in_studio.length}
            names={buckets.in_studio.map(m => m.name)} color="emerald"
            testId="bucket-in-studio" />
          <Bucket icon={Camera} label="On Shoot" count={buckets.on_shoot.length}
            names={buckets.on_shoot.map(m => m.name)} color="blue"
            testId="bucket-on-shoot" />
          <Bucket icon={Home} label="WFH" count={buckets.wfh.length}
            names={buckets.wfh.map(m => m.name)} color="purple"
            testId="bucket-wfh" />
          <Bucket icon={Umbrella} label="On Leave" count={buckets.leave.length}
            names={buckets.leave.map(m => m.name)} color="amber"
            testId="bucket-leave" />
          <Bucket icon={UserX} label="Not Marked" count={buckets.absent.length}
            names={buckets.absent.map(m => m.name)} color="zinc"
            testId="bucket-absent" muted />
        </div>
      </CardContent>
    </Card>
  );
};

const COLOR_MAP = {
  emerald: { border: 'border-emerald-500/30', bg: 'bg-emerald-500/5', text: 'text-emerald-400', num: 'text-emerald-300' },
  blue: { border: 'border-blue-500/30', bg: 'bg-blue-500/5', text: 'text-blue-400', num: 'text-blue-300' },
  purple: { border: 'border-purple-500/30', bg: 'bg-purple-500/5', text: 'text-purple-400', num: 'text-purple-300' },
  amber: { border: 'border-amber-500/30', bg: 'bg-amber-500/5', text: 'text-amber-400', num: 'text-amber-300' },
  zinc: { border: 'border-zinc-500/20', bg: 'bg-zinc-500/5', text: 'text-zinc-500', num: 'text-zinc-400' },
};

const Bucket = ({ icon: Icon, label, count, names, color, testId, muted }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.zinc;
  return (
    <div className={`p-3 rounded-lg border ${c.border} ${c.bg}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`h-3.5 w-3.5 ${c.text}`} />
        <span className={`text-[10px] uppercase tracking-wider ${c.text}`}>{label}</span>
      </div>
      <p className={`text-2xl font-semibold ${muted ? c.num : c.num}`}>{count}</p>
      <div className={`mt-1 text-[10px] leading-tight ${muted ? 'text-zinc-500' : 'text-white/70'} min-h-[24px]`}>
        {names.length === 0 ? <span className="text-zinc-600">—</span> : names.slice(0, 4).join(', ') + (names.length > 4 ? ` +${names.length - 4}` : '')}
      </div>
    </div>
  );
};

export default TeamNowWidget;
